import { createFileRoute, Outlet } from '@tanstack/react-router'

import { NotFoundState } from '@/components/common/NotFoundState'
import { AppShell, AppShellSkeleton } from '@/components/layout/AppShell'
import { NoAppAccess } from '@/components/layout/NoAppAccess'
import { sessionGate } from '@/components/layout/sessionGate'

/**
 * The authenticated frame.
 *
 * Pathless (`_app`), so it contributes no URL segment — `/` and
 * `/organizations` are unchanged. It exists so that a page *outside* the
 * application, like login, can render without the sidebar; when the shell
 * lived on `__root` there was nowhere for such a page to go.
 *
 * `pendingComponent` is not decoration. `autoCodeSplitting` puts this layout in
 * a lazy chunk, where it used to be part of the entry bundle — so without it a
 * cold load shows a blank frame before the sidebar appears.
 *
 * Do not add `validateSearch` here: TanStack merges a parent's search schema
 * into every descendant, which would widen `useSearch()` on every page under it.
 */
export const Route = createFileRoute('/_app')({
  /**
   * The guard, on the layout rather than on each page.
   *
   * `beforeLoad` runs before any child loader, so an unauthenticated visit
   * never issues the queries the page would have made — which is what stops a
   * redirect being preceded by a flash of empty tables and a row of 401s in
   * the network panel.
   *
   * `restore()` is awaited because a cold load legitimately has no token yet:
   * the access token lives in memory and a reload clears it. Redirecting on
   * "no token in memory" would sign the user out on every refresh.
   *
   * The current URL travels as `redirect` so that following a deep link, then
   * signing in, lands where it was going.
   */
  beforeLoad: async ({ location }) => {
    const gate = await sessionGate()
    if (gate === 'in') return { noAccess: false }
    // Signed in, but not to VentaWiz: say so, rather than send somebody with
    // a perfectly good session back to a sign-in form.
    if (gate === 'no_access') return { noAccess: true }

    // A **full page navigation**, not a router redirect. Sign-in lives in
    // VentaWiz-Auth's own UI now — it owns the login ticket, the four stages
    // and the copy for each refusal — and that is a separate bundle served
    // from `/auth` on this same origin. There is no client-side route to it.
    //
    // Same origin is what makes one sign-in cover every portal: the refresh
    // cookie is `__Host-` and host-only, so an auth app on its own hostname
    // could not hand this app a session.
    //
    // The whole `href` travels, search string included: a saved filter or a
    // chosen page is in there, and dropping it returns the user to a bare page
    // that is not the one they asked for.
    window.location.assign(`/auth/login?redirect=${encodeURIComponent(location.href)}`)

    // The navigation is asynchronous, so without this the router carries on
    // and renders the shell for an unauthenticated user for one frame — a
    // flash of the app they are not signed in to.
    await new Promise(() => {
      /* never settles; the page is leaving */
    })
  },
  component: AppRoute,
  pendingComponent: AppShellSkeleton,
  // Inside the shell: an address that matches no page keeps the sidebar, so
  // the way back is where it always is.
  notFoundComponent: NotFoundState,
})

function AppRoute() {
  // Only the two branches that stay return context; the third is leaving.
  const context = Route.useRouteContext()
  if ('noAccess' in context && context.noAccess) return <NoAppAccess />
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}
